import { useParams } from 'react-router-dom';
import { useMutation, useApolloClient } from 'react-apollo';

import { AlbumFolderTags } from '../../../../../graphql/queries/album-folder-tags';
import {
  albumFolderTagUpdate as albumFolderTagUpdateMutation,
  uniformAlbumFolderTagLevelTeamsCreate as uniformAlbumFolderTagLevelTeamsCreateMutation,
} from '../../../../../graphql/mutations/album-folder-tags';
import { ALBUM_FOLDER_TAG_TYPE } from '../../../../../constants/album-folder-tags';
import { capitalize } from '../../../../../util/text';

/**
 * Hook to generate sport/level/team combinations that are missing from the album folder.
 * Used when importing roster identities from CSV data.
 *
 * @param {Object} options
 * @param {string} [options.albumFolderId] - The album folder ID to use. Falls back to folderId from route params.
 * @returns {Object} Object containing generateSportCombinations function and loading state
 */
const useUniformSportGeneration = ({ albumFolderId }) => {
  const { folderId } = useParams();
  const entityId = albumFolderId ?? folderId;
  const client = useApolloClient();
  const [albumFolderTagUpdate] = useMutation(albumFolderTagUpdateMutation);
  const [uniformAlbumFolderTagLevelTeamsCreate] = useMutation(
    uniformAlbumFolderTagLevelTeamsCreateMutation,
  );

  /**
   * Fetches the latest album folder tags data  ``directly from the server.
   *
   * @returns {Promise<Array>} The array of tag records
   */
  const fetchLatestTags = async () => {
    const { data } = await client.query({
      query: AlbumFolderTags,
      variables: { id: entityId },
      fetchPolicy: 'network-only',
    });
    return data?.albumFolder?.tags?.records ?? [];
  };

  /**
   * Finds an existing uniform tag by sport name and gender.
   *
   * @param {Array} tags - The available tags array
   * @param {string} sport - The sport name to find
   * @param {string} [gender] - The gender to match (optional)
   * @returns {Object|undefined} The matching album folder tag or undefined
   */
  const findExistingTag = (tags, sport, gender) => {
    return tags.find(
      tag =>
        tag.type === ALBUM_FOLDER_TAG_TYPE.UNIFORM &&
        capitalize((tag.name ?? '').toLowerCase().trim()) ===
          capitalize((sport ?? '').toLowerCase().trim()) &&
        (!gender || tag.gender === gender),
    );
  };

  /**
   * Finds an existing level within a tag by level name.
   *
   * @param {Object} tag - The album folder tag to search within
   * @param {string} levelName - The level name to find
   * @returns {Object|undefined} The matching level or undefined
   */
  const findExistingLevel = (tag, levelName) => {
    const { records: levels = [] } = tag?.levels ?? {};
    return levels.find(
      level =>
        capitalize((level.name ?? '').toLowerCase().trim()) ===
        capitalize((levelName ?? '').toLowerCase().trim()),
    );
  };

  /**
   * Finds an existing team within a level by team name.
   *
   * @param {Object} level - The level to search within
   * @param {string} teamName - The team name to find
   * @returns {Object|undefined} The matching team or undefined
   */
  const findExistingTeam = (level, teamName) => {
    const { records: teams = [] } = level?.teams ?? {};
    return teams.find(
      team =>
        capitalize((team.name ?? '').toLowerCase().trim()) ===
        capitalize((teamName ?? '').toLowerCase().trim()),
    );
  };

  /**
   * Creates a new level for a given tag.
   *
   * @param {string} tagId - The album folder tag ID
   * @param {string} levelName - The name of the level to create
   * @returns {Promise<Object>} The created level
   */
  const createLevel = async (tagId, levelName) => {
    const {
      data: {
        albumFolderTagUpdate: { album_folder_tag: updatedTag },
      },
    } = await albumFolderTagUpdate({
      variables: {
        input: {
          id: tagId,
          uniform: { create_levels: levelName },
        },
      },
    });

    // Find the newly created level from the mutation response
    const createdLevel = updatedTag.levels.records.find(
      level =>
        capitalize((level.name ?? '').toLowerCase().trim()) ===
        capitalize((levelName ?? '').toLowerCase().trim()),
    );

    return createdLevel;
  };

  /**
   * Creates a new team for a given level.
   *
   * @param {string} levelId - The uniform album folder tag level ID
   * @param {string} teamName - The name of the team to create
   * @returns {Promise<Object>} The created team
   */
  const createTeam = async (levelId, teamName) => {
    const {
      data: {
        uniformAlbumFolderTagLevelTeamsCreate: {
          uniform_album_folder_tag_level: updatedLevel,
        },
      },
    } = await uniformAlbumFolderTagLevelTeamsCreate({
      variables: {
        input: {
          uniform_album_folder_tag_level_id: levelId,
          teams: [{ name: teamName }],
        },
      },
    });

    // Find the newly created team from the mutation response
    const createdTeam = (updatedLevel?.teams?.records ?? []).find(
      team =>
        capitalize((team.name ?? '').toLowerCase().trim()) ===
        capitalize((teamName ?? '').toLowerCase().trim()),
    );

    return createdTeam;
  };

  /**
   * Generates sport/level/team combinations that are missing.
   * For each missing combination, creates the necessary levels and teams.
   *
   * @param {Array<Object>} missingCombinations - Array of missing sport combinations
   * @param {string} missingCombinations[].sport - The sport name
   * @param {string} [missingCombinations[].gender] - The gender (optional)
   * @param {string} missingCombinations[].level - The level name
   * @param {string} [missingCombinations[].team] - The team name (optional)
   * @returns {Promise<Map>} A map from combination key to the generated selector option data
   */
  const generateSportCombinations = async (missingCombinations = []) => {
    if (!missingCombinations || missingCombinations.length === 0) {
      return new Map();
    }

    // Fetch the latest data before processing
    let tags = await fetchLatestTags();
    const newSportCombinations = new Map();

    for (let i = 0; i < missingCombinations.length; i += 1) {
      const {
        sport,
        gender,
        level: levelName,
        team: teamName,
      } = missingCombinations[i];

      // Create a unique key for this combination
      const combinationKey = `${(sport ?? '').toLowerCase()}-${(
        gender ?? ''
      ).toLowerCase()}-${(levelName ?? '').toLowerCase()}-${(
        teamName ?? ''
      ).toLowerCase()}`;

      // Skip if we've already processed this combination
      if (newSportCombinations.has(combinationKey)) {
        continue;
      }

      // Find the existing tag for this sport/gender
      let tag = findExistingTag(tags, sport, gender);

      if (!tag) {
        // If no tag exists for this sport, we cannot create it automatically
        // Sports must be pre-configured in the gallery
        continue;
      }

      // Find or create the level
      let level = findExistingLevel(tag, levelName);

      if (!level && levelName) {
        level = await createLevel(tag.id, levelName);
        // Fetch fresh data after creating the level
        tags = await fetchLatestTags();
        // Re-find the tag with updated data
        tag = findExistingTag(tags, sport, gender);
      }

      // Find or create the team (if team name is provided)
      let team = null;
      if (teamName && level) {
        // Re-find the level from updated tag
        level = findExistingLevel(tag, levelName);
        team = findExistingTeam(level, teamName);

        if (!team) {
          team = await createTeam(level.id, teamName);
          // Fetch fresh data after creating the team
          tags = await fetchLatestTags();
          // Re-find everything with updated data
          tag = findExistingTag(tags, sport, gender);
          level = findExistingLevel(tag, levelName);
          team = findExistingTeam(level, teamName);
        }
      }

      // Build the selector option
      if (tag && level) {
        const selectorOption = {
          sport: capitalize((tag.name ?? '').toLowerCase().trim()),
          gender: tag.gender,
          level: capitalize((level.name ?? '').toLowerCase().trim()),
          team: team ? capitalize((team.name ?? '').toLowerCase().trim()) : '',
          album_folder_tag_id: tag.id,
          uniform_album_folder_tag_level_id: level.id,
          uniform_album_folder_tag_level_team_id: team?.id,
        };

        newSportCombinations.set(combinationKey, selectorOption);
      }
    }

    return newSportCombinations;
  };

  return {
    generateSportCombinations,
  };
};

export default useUniformSportGeneration;
